import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarRange, CheckCircle2, Megaphone, Radar, RefreshCw, Target, TrendingUp, Wallet } from 'lucide-react';
import { toast } from '../ui/sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MetricCard = ({ label, value, helper, Icon, testId }) => (
  <div className="glass-card p-5" data-testid={testId}>
    <div className="flex items-center justify-between gap-3">
      <div>
        <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{label}</p>
        <p className="mt-3 text-3xl font-bold text-text-heading">{value}</p>
        <p className="mt-2 text-xs text-text-muted">{helper}</p>
      </div>
      <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary"><Icon className="h-5 w-5" /></div>
    </div>
  </div>
);

const readinessStyle = (status) => {
  if (['ready', 'connected'].includes(status)) return 'border border-primary/20 bg-primary/10 text-primary';
  if (['mapped', 'ready_to_connect'].includes(status)) return 'border border-sky-400/20 bg-sky-500/10 text-sky-200';
  return 'border border-amber-400/20 bg-amber-500/10 text-amber-200';
};

export default function CampaignWarRoomHub({ authFetch }) {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [activeView, setActiveView] = useState('launch');

  const loadData = useCallback(async (showToast = false) => {
    try {
      setRefreshing(true);
      const res = await authFetch(`${API}/admin/campaign-war-room`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'No se pudo cargar el War Room');
      setData(json);
      if (showToast) toast.success('War Room actualizado');
    } catch (error) {
      toast.error(error.message || 'No se pudo cargar el War Room');
    } finally {
      setRefreshing(false);
    }
  }, [authFetch]);

  useEffect(() => {
    loadData(false);
  }, [loadData]);

  const readinessCounts = useMemo(() => {
    const items = data?.readiness_board || [];
    return {
      ready: items.filter((item) => ['ready', 'connected'].includes(item.status)).length,
      pending: items.filter((item) => ['mapped', 'ready_to_connect'].includes(item.status)).length,
      blocked: items.filter((item) => !['ready', 'connected', 'mapped', 'ready_to_connect'].includes(item.status)).length,
    };
  }, [data]);

  if (!data) return <div className="glass-card p-8 text-center text-text-muted" data-testid="campaign-war-room-loading">Cargando War Room...</div>;

  return (
    <div className="space-y-6" data-testid="campaign-war-room-hub">
      <div className="glass-card tracing-panel overflow-hidden" data-testid="campaign-war-room-hero-card">
        <div className="grid gap-6 p-6 xl:grid-cols-[1.08fr_0.92fr] xl:p-8">
          <div>
            <p className="editorial-kicker mb-3">Campaign War Room</p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Diagnóstico aplicado · launch cockpit modular</p>
            <h3 className="mt-4 text-3xl font-semibold text-text-heading">Centro de mando para lanzar orgánico y convertirlo en ventas</h3>
            <p className="mt-3 max-w-3xl text-sm text-text-body">El cambio aquí no es de lógica, sino de velocidad operativa: separar lanzamiento, assets, pipeline y respuesta comercial para que dirección vea el estado real en segundos.</p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="campaign-war-room-diagnosis-chips">
              <span className="insight-chip">Launch cockpit</span>
              <span className="insight-chip">Assets visibles</span>
              <span className="insight-chip">Pipeline claro</span>
              <span className="insight-chip">Respuesta comercial</span>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-2">
            <MetricCard label="Assets listos" value={data.summary.assets_ready} helper="base orgánica preparada" Icon={Megaphone} testId="campaign-war-room-metric-assets" />
            <MetricCard label="Leads recientes" value={data.summary.recent_leads} helper="interés detectado" Icon={Target} testId="campaign-war-room-metric-leads" />
            <MetricCard label="Citas" value={data.summary.recent_bookings} helper="conversaciones activas" Icon={CalendarRange} testId="campaign-war-room-metric-bookings" />
            <MetricCard label="Pagos" value={data.summary.paid_transactions} helper="conversión real" Icon={Wallet} testId="campaign-war-room-metric-paid" />
          </div>
        </div>
      </div>

      <div className="glass-card tracing-panel p-6" data-testid="campaign-war-room-tabs-shell">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Navigation layer</p>
            <h4 className="mt-2 text-xl font-semibold text-text-heading">Separa el launch en cuatro vistas para decidir más rápido</h4>
          </div>
          <button type="button" onClick={() => loadData(true)} className="btn-secondary" data-testid="campaign-war-room-refresh-button">
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /> Refrescar
          </button>
        </div>

        <Tabs value={activeView} onValueChange={setActiveView} className="mt-5" data-testid="campaign-war-room-tabs-root">
          <TabsList className="flex h-auto w-full flex-wrap justify-start gap-2 rounded-[20px] border border-border-subtle bg-background/40 p-2">
            <TabsTrigger value="launch" className="rounded-full px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-black" data-testid="campaign-war-room-tab-launch">Launch</TabsTrigger>
            <TabsTrigger value="assets" className="rounded-full px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-black" data-testid="campaign-war-room-tab-assets">Assets</TabsTrigger>
            <TabsTrigger value="calendar" className="rounded-full px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-black" data-testid="campaign-war-room-tab-calendar">Calendario</TabsTrigger>
            <TabsTrigger value="pipeline" className="rounded-full px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-black" data-testid="campaign-war-room-tab-pipeline">Pipeline</TabsTrigger>
          </TabsList>

          <TabsContent value="launch" className="mt-5 space-y-6" data-testid="campaign-war-room-view-launch">
            <div className="grid gap-6 xl:grid-cols-[0.88fr_1.12fr]">
              <div className="glass-card p-6" data-testid="campaign-war-room-readiness-card">
                <div className="flex items-center gap-2 text-text-heading"><Target className="h-5 w-5 text-primary" /><h4 className="text-lg font-semibold">Radar de readiness</h4></div>
                <div className="mt-4 grid gap-4 md:grid-cols-3">
                  <MetricCard label="Listo" value={readinessCounts.ready} helper="bloques activos" Icon={CheckCircle2} testId="campaign-war-room-ready-count" />
                  <MetricCard label="Pendiente" value={readinessCounts.pending} helper="requiere último paso" Icon={Radar} testId="campaign-war-room-pending-count" />
                  <MetricCard label="Bloqueado" value={readinessCounts.blocked} helper="necesita decisión o credencial" Icon={TrendingUp} testId="campaign-war-room-blocked-count" />
                </div>
                <div className="mt-4 space-y-3">
                  {(data.readiness_board || []).map((item, index) => (
                    <div key={`${item.label}-${index}`} className="rounded-[18px] border border-border-subtle bg-background/55 p-4" data-testid={`campaign-war-room-readiness-item-${index}`}>
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-semibold text-text-heading">{item.label}</p>
                        <span className={`rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.16em] ${readinessStyle(item.status)}`} data-testid={`campaign-war-room-readiness-status-${index}`}>{item.status}</span>
                      </div>
                      <p className="mt-2 text-xs text-text-body">{item.detail}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="glass-card p-6" data-testid="campaign-war-room-playbook-card">
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Checklist y respuesta rápida</p>
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <div data-testid="campaign-war-room-publishing-checklist">
                    <p className="text-sm font-semibold text-text-heading">Antes de publicar</p>
                    <div className="mt-3 space-y-2">
                      {(data.operations_kit?.publishing_checklist || []).map((item, index) => (
                        <div key={index} className="flex items-start gap-3 rounded-[18px] border border-border-subtle bg-background/55 p-3" data-testid={`campaign-war-room-checklist-item-${index}`}>
                          <CheckCircle2 className="mt-0.5 h-4 w-4 text-primary" />
                          <p className="text-xs text-text-body">{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div data-testid="campaign-war-room-lead-playbook">
                    <p className="text-sm font-semibold text-text-heading">Guion de respuesta a leads</p>
                    <div className="mt-3 space-y-2">
                      {(data.operations_kit?.lead_response_playbook || []).map((item, index) => (
                        <div key={index} className="rounded-[18px] border border-border-subtle bg-background/55 p-3" data-testid={`campaign-war-room-lead-playbook-item-${index}`}>
                          <p className="text-xs text-text-body">{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="assets" className="mt-5 space-y-6" data-testid="campaign-war-room-view-assets">
            <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
              <div className="glass-card p-6" data-testid="campaign-war-room-assets-card">
                <div className="flex items-center gap-2 text-text-heading"><Megaphone className="h-5 w-5 text-primary" /><h4 className="text-lg font-semibold">Assets de lanzamiento ya listos</h4></div>
                <div className="mt-5 grid gap-4 lg:grid-cols-2">
                  <div data-testid="campaign-war-room-videos-list">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Vídeos V6</p>
                    <div className="mt-3 space-y-3">
                      {(data.launch_assets?.videos || []).map((item, index) => (
                        <div key={item.slug} className="rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid={`campaign-war-room-video-${index}`}>
                          <p className="text-sm font-semibold text-text-heading">{item.title}</p>
                          <p className="mt-1 text-xs text-text-body">{item.hook}</p>
                          <a href={item.url} target="_blank" rel="noreferrer" className="mt-3 inline-flex text-xs font-semibold text-primary underline-offset-4 hover:underline" data-testid={`campaign-war-room-video-link-${index}`}>Abrir MP4</a>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div data-testid="campaign-war-room-posters-list">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Posters premium</p>
                    <div className="mt-3 space-y-3">
                      {(data.launch_assets?.posters || []).map((item, index) => (
                        <div key={item.slug} className="rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid={`campaign-war-room-poster-${index}`}>
                          <p className="text-sm font-semibold text-text-heading">{item.title}</p>
                          <a href={item.url} target="_blank" rel="noreferrer" className="mt-3 inline-flex text-xs font-semibold text-primary underline-offset-4 hover:underline" data-testid={`campaign-war-room-poster-link-${index}`}>Ver visual</a>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="glass-card p-6" data-testid="campaign-war-room-operations-kit-card">
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Kits internos</p>
                <div className="mt-4 space-y-3">
                  {(data.operations_kit?.documents || []).map((item, index) => (
                    <div key={`${item.title}-${index}`} className="rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid={`campaign-war-room-kit-${index}`}>
                      <p className="text-sm font-semibold text-text-heading">{item.title}</p>
                      <p className="mt-2 text-xs text-text-body">{item.summary}</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {(item.focus || []).map((focus) => <span key={focus} className="rounded-full border border-border-subtle px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-text-muted">{focus}</span>)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="calendar" className="mt-5 space-y-6" data-testid="campaign-war-room-view-calendar">
            <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
              <div className="glass-card p-6" data-testid="campaign-war-room-calendar-card">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Calendario editorial</p>
                  <h4 className="mt-2 text-xl font-semibold text-text-heading">Plan de publicación semanal</h4>
                </div>
                <div className="mt-5 space-y-3">
                  {(data.war_room.editorial_calendar || []).map((item, index) => (
                    <div key={index} className="rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid={`campaign-war-room-calendar-item-${index}`}>
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-text-heading">{item.day} · {item.theme}</p>
                          <p className="mt-1 text-xs text-text-muted">Formato: {item.format}</p>
                        </div>
                        <span className="rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[10px] uppercase tracking-[0.16em] text-primary">{item.cta}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="glass-card p-6" data-testid="campaign-war-room-seven-day-card">
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Calendario operativo 7 días</p>
                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  {(data.operations_kit?.seven_day_calendar || []).map((item, index) => (
                    <div key={item.day} className="rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid={`campaign-war-room-seven-day-item-${index}`}>
                      <p className="text-sm font-semibold text-text-heading">{item.day}</p>
                      <p className="mt-2 text-xs uppercase tracking-[0.16em] text-primary">{item.focus}</p>
                      <p className="mt-2 text-xs text-text-body">{item.channel}</p>
                      <p className="mt-2 text-xs text-text-muted">{item.asset}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="pipeline" className="mt-5 space-y-6" data-testid="campaign-war-room-view-pipeline">
            <div className="grid gap-6 xl:grid-cols-[1fr_1fr]">
              <div className="glass-card p-6" data-testid="campaign-war-room-review-pipeline-card">
                <div className="flex items-center gap-2 text-text-heading"><Radar className="h-5 w-5 text-primary" /><h4 className="text-lg font-semibold">Pipeline de review premium</h4></div>
                <div className="mt-4 space-y-3 text-sm text-text-body">
                  <p data-testid="campaign-war-room-review-status">Estado Frame.io: <strong>{data.review_pipeline?.status || 'pendiente'}</strong></p>
                  <p data-testid="campaign-war-room-review-project">Proyecto raíz: <strong>{data.review_pipeline?.frameio_project_path || 'sin mapear'}</strong></p>
                  <p data-testid="campaign-war-room-review-account">Account ID: <strong>{data.review_pipeline?.frameio_account_id || 'sin cuenta'}</strong></p>
                  <p data-testid="campaign-war-room-review-next-step">{data.review_pipeline?.next_step}</p>
                </div>
              </div>

              <div className="glass-card p-6" data-testid="campaign-war-room-cta-card">
                <div className="flex items-center gap-2 text-text-heading"><Radar className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">CTA por red + gaps</h4></div>
                <div className="mt-4 space-y-3">
                  {(data.war_room.cta_matrix || []).map((item, index) => (
                    <div key={index} className="rounded-[18px] border border-border-subtle bg-background/55 p-4" data-testid={`campaign-war-room-cta-item-${index}`}>
                      <p className="text-sm font-semibold text-text-heading">{item.channel}</p>
                      <p className="mt-1 text-xs text-text-muted">CTA: {item.primary_cta}</p>
                      <p className="mt-1 text-xs text-text-body">Oferta: {item.offer}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-5 space-y-3">
                  {(data.war_room.launch_gaps || []).map((item, index) => (
                    <div key={index} className="flex items-start gap-3 rounded-[18px] border border-border-subtle bg-background/55 p-4" data-testid={`campaign-war-room-gap-${index}`}>
                      <CheckCircle2 className="mt-0.5 h-4 w-4 text-primary" />
                      <p className="text-sm text-text-body">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid gap-6 xl:grid-cols-3">
              <div className="glass-card p-6" data-testid="campaign-war-room-leads-card">
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Leads</p>
                <div className="mt-4 space-y-3">
                  {(data.recent_leads || []).length ? data.recent_leads.map((lead, index) => (
                    <div key={lead.id || index} className="rounded-[18px] border border-border-subtle bg-background/55 p-4" data-testid={`campaign-war-room-lead-${index}`}>
                      <p className="text-sm font-semibold text-text-heading">{lead.name || lead.nombre || 'Lead'}</p>
                      <p className="mt-1 text-xs text-text-muted">{lead.email || 'sin email'}</p>
                    </div>
                  )) : <p className="text-sm text-text-muted">Sin leads aún.</p>}
                </div>
              </div>

              <div className="glass-card p-6" data-testid="campaign-war-room-bookings-card">
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Citas</p>
                <div className="mt-4 space-y-3">
                  {(data.recent_bookings || []).length ? data.recent_bookings.map((booking, index) => (
                    <div key={booking.id || index} className="rounded-[18px] border border-border-subtle bg-background/55 p-4" data-testid={`campaign-war-room-booking-${index}`}>
                      <p className="text-sm font-semibold text-text-heading">{booking.nombre || booking.name || 'Reserva'}</p>
                      <p className="mt-1 text-xs text-text-muted">{booking.fecha || 'sin fecha'} {booking.hora || ''}</p>
                    </div>
                  )) : <p className="text-sm text-text-muted">Sin citas aún.</p>}
                </div>
              </div>

              <div className="glass-card p-6" data-testid="campaign-war-room-payments-card">
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Pagos cerrados</p>
                <div className="mt-4 space-y-3">
                  {(data.recent_paid_transactions || []).length ? data.recent_paid_transactions.map((tx, index) => (
                    <div key={tx.id || tx.session_id || index} className="rounded-[18px] border border-border-subtle bg-background/55 p-4" data-testid={`campaign-war-room-payment-${index}`}>
                      <p className="text-sm font-semibold text-text-heading">{tx.customer_email || 'Pago'}</p>
                      <p className="mt-1 text-xs text-text-muted">{tx.amount_total || tx.amount || 0} {tx.currency || 'usd'}</p>
                    </div>
                  )) : <p className="text-sm text-text-muted">Sin pagos cerrados aún.</p>}
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}